/**
 * questline — the Tutor's onboarding quests. A fixed, ordered list the player
 * works through one at a time. Quests give NO reward; they only teach the game
 * systems. Progress (which quest you're on) is persisted in the save.
 *
 * `questStep` semantics (in GameState):
 *   0             → questline not started
 *   1..QUEST_COUNT→ actively working on QUESTS[step-1]
 *   QUEST_COUNT+1 → showing the closing (graduation) dialog
 *   99            → fully finished
 */
export interface QuestDef {
  id: number; // 1..QUEST_COUNT
  title: string; // short label for the tracker
  objective: string; // one-line objective for the tracker
  give: string[]; // Tutor lines shown when the quest is handed out (why + how)
}

/** The Tutor introduces herself, then hands out the first quest. */
export const INTRO_LINES: string[] = [
  "Hello, Commander! I'm Aria, your onboard flight tutor.",
  "I'll walk you through the basics so you can run this mining fleet like a veteran.",
  "I'll give you a series of short quests. They give no reward — they're only here to teach you the ropes.",
  "Follow them and you'll be self-sufficient in no time. Let's begin!",
];

/** Shown when the final quest is completed (questStep QUEST_COUNT+1). */
export const ENDING_LINES: string[] = [
  "That's the end of my lessons, Commander.",
  "You now know how to mine, upgrade, trade, craft and defend your ship.",
  "The frontier is yours to conquer. Fly safe out there — Aria, signing off!",
];

/** Prefixed to the next quest's lines whenever a quest is completed. */
export const PRAISE_LINE = "Nicely done, Commander! Here's what's next.";

export const QUESTS: QuestDef[] = [
  {
    id: 1,
    title: "Find Safe Harbor",
    objective: "Fly to a highlighted safe-zone planet",
    give: [
      "First, fly to one of the highlighted safe-zone planets — they're marked on your map and minimap.",
      "Safe zones have no raiders, so you can mine in peace and dock for services.",
    ],
  },
  {
    id: 2,
    title: "First Minerals",
    objective: "Mine until you have 100 minerals",
    give: [
      "Now aim at an asteroid to fire your mining laser. Minerals are the lifeblood of your fleet.",
      "Gather 100 minerals — just enough for your very first upgrade.",
    ],
  },
  {
    id: 3,
    title: "First Upgrade",
    objective: "Buy your first upgrade",
    give: [
      "Open the Upgrades panel and buy your first one — Mining Laser I is the perfect start.",
      "Upgrades make you mine faster, fly farther and hit harder. Keep buying them whenever you can.",
    ],
  },
  {
    id: 4,
    title: "Level Up",
    objective: "Reach Level 2",
    give: [
      "Mining and fighting earn XP. Fill the XP bar to reach Level 2.",
      "Every level grants a Skill Point ★ — and some upgrades need those to unlock.",
    ],
  },
  {
    id: 5,
    title: "A Little Trade",
    objective: "Get 2 Units at a Trade Hub",
    give: [
      "Pop over to a Trade Hub (the 🛒 planet) and sell a little ore for 2 Units.",
      "You'll spend those Units on a special upgrade at the mechanical yard.",
    ],
  },
  {
    id: 6,
    title: "Mechanical Yard",
    objective: "Dock at a Mechanical yard (🔧)",
    give: [
      "Now fly to a Mechanical yard — the 🔧 planet.",
      "Certain upgrades and every hull refit can ONLY be built here at the yard.",
    ],
  },
  {
    id: 7,
    title: "Third Beam",
    objective: "Unlock the Tri-Beam Array at the yard",
    give: [
      "At the yard, open ⚙ Upgrades and unlock the Tri-Beam Array — a third mining beam.",
      "See how it needs Units, a Skill Point and the yard itself? That's how mechanical upgrades work.",
    ],
  },
  {
    id: 8,
    title: "Toward Mk II",
    objective: "Unlock the Mothership Mk II refit",
    give: [
      "Keep buying upgrades along the Laser, Optics and Engine lines.",
      "Once those lines are complete, the Mothership Mk II refit unlocks — a far stronger hull.",
    ],
  },
  {
    id: 9,
    title: "Trade for Units",
    objective: "Get 20 Units at a Trade Hub",
    give: [
      "Dock at a Trade Hub (the 🛒 planet) and sell minerals for Units.",
      "Units are premium currency for turrets, crew and fighters. Trade until you hold 20.",
    ],
  },
  {
    id: 10,
    title: "Moon Cores",
    objective: "Collect 2 Moon Cores",
    give: [
      "Fly to a moon and mine it down. Moons sometimes drop rare Moon Cores.",
      "Moon Cores are prized crafting materials. Collect 2 of them.",
    ],
  },
  {
    id: 11,
    title: "Energy Core",
    objective: "Craft 1 Energy Core",
    give: [
      "At a Mechanical yard (the 🔧 planet), craft an Energy Core from your materials.",
      "Energy Cores power the big hull refits — you'll need one for the Mk II jump.",
    ],
  },
  {
    id: 12,
    title: "Mk II Refit",
    objective: "Refit to Mothership Mk II",
    give: [
      "Now buy the Mothership Mk II refit in the Upgrades panel.",
      "Mk II is bigger and tougher, and it unlocks weapon hardpoints and drone bays.",
    ],
  },
  {
    id: 13,
    title: "Turret Mount",
    objective: "Unlock a turret slot",
    give: [
      "At the Mechanical yard, open the Arsenal and unlock a turret mount.",
      "Hardpoints let you bolt automatic guns onto your hull.",
    ],
  },
  {
    id: 14,
    title: "Arm Up",
    objective: "Buy or craft a turret",
    give: [
      "Buy a turret from the Arsenal (or craft one from parts).",
      "A turret auto-targets raiders, so you can keep mining while it fights for you.",
    ],
  },
  {
    id: 15,
    title: "Lock & Load",
    objective: "Equip the turret into a mount",
    give: [
      "Finally, equip the turret into your unlocked mount.",
      "Your Mothership can now defend itself. You're ready for the frontier!",
    ],
  },
  {
    id: 16,
    title: "To the Vast Space!",
    objective: "Craft Warp Fuel, then Warp to a new system",
    give: [
      "One final lesson, Commander. At a Mechanical yard, craft Warp Drive Fuel (⛽) from Dark Matter and Units.",
      "Then press the Warp ▸ button to jump to a brand-new solar system — richer, tougher and packed with loot.",
      "Each jump makes the galaxy more dangerous and more rewarding. This is how you truly grow. Off you go!",
    ],
  },
];

export const QUEST_COUNT = QUESTS.length; // actionable quests

export function questDef(step: number): QuestDef | null {
  return step >= 1 && step <= QUEST_COUNT ? QUESTS[step - 1]! : null;
}
